import type { Vector2 } from './Vector2';

export type TeamSide = 0 | 1;
export type FieldThird = 'DEFENSIVE' | 'MIDDLE' | 'FINAL';
export type LateralChannel = 'LEFT_WING' | 'LEFT_HALF_SPACE' | 'CENTRAL' | 'RIGHT_HALF_SPACE' | 'RIGHT_WING';

export interface PitchRectangle {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

const clamp = (value: number, minimum: number, maximum: number): number => (
  Math.max(minimum, Math.min(maximum, value))
);

/**
 * Single authority for the engine's normalized 0..100 pitch.
 *
 * The values intentionally preserve utility-ai-1.8.0 behaviour. Logical
 * areas are tactical regions, not a claim that the simulation uses exact
 * real-world metre dimensions.
 */
export class PitchGeometry {
  readonly length = 100;
  readonly width = 100;
  readonly centre: Vector2 = { x: 50, y: 50 };
  readonly boundaryLines = { homeGoalX: 0, halfwayX: 50, awayGoalX: 100, topTouchlineY: 0, bottomTouchlineY: 100 } as const;
  readonly corners: readonly Vector2[] = [
    { x: 0, y: 0 }, { x: 0, y: 100 }, { x: 100, y: 0 }, { x: 100, y: 100 },
  ];
  readonly playableBounds: PitchRectangle = { minX: 1, maxX: 99, minY: 2, maxY: 98 };
  readonly restartBounds: PitchRectangle = { minX: 3, maxX: 97, minY: 4, maxY: 96 };
  readonly predictionBounds: PitchRectangle = { minX: 2, maxX: 98, minY: 3, maxY: 97 };
  readonly fieldThirds = { defensiveEnd: 34, finalStart: 68 } as const;
  readonly functionalProgress = { ownHalfEnd: 50, offensiveStart: 58, shootingStart: 82 } as const;
  readonly lateralBands = { leftWingEnd: 25, leftHalfSpaceEnd: 40, centralEnd: 60, rightHalfSpaceEnd: 75 } as const;
  readonly extremeWide = { maximumLeftY: 7, minimumRightY: 93 } as const;
  readonly penaltyArea = { depth: 18, minY: 28, maxY: 72 } as const;
  readonly goalArea = { depth: 6, minY: 37, maxY: 63 } as const;
  readonly goalMouth = { minY: 37, maxY: 63 } as const;
  readonly goalkeeper = {
    normalMinProgress: 1,
    normalMaxProgress: 11,
    sweepMaxProgress: 15,
    physicalMinProgress: 1.5,
    physicalMaxProgress: 13.5,
    minY: 39,
    maxY: 61,
    emergencyAreaProgress: 28,
  } as const;
  readonly shotTarget = { homeX: 3.5, awayX: 96.5, minY: 37, maxY: 63 } as const;

  relativeProgress(side: TeamSide, worldX: number): number {
    return side === 0 ? worldX : this.length - worldX;
  }

  worldX(side: TeamSide, progress: number): number {
    return side === 0 ? progress : this.length - progress;
  }

  attackingGoalX(side: TeamSide): number {
    return side === 0 ? this.shotTarget.awayX : this.shotTarget.homeX;
  }

  ownGoalCentre(side: TeamSide): Vector2 {
    return { x: this.worldX(side, this.boundaryLines.homeGoalX), y: this.centre.y };
  }

  attackingGoalCentre(side: TeamSide): Vector2 {
    return { x: this.worldX(side, this.boundaryLines.awayGoalX), y: this.centre.y };
  }

  classifyThird(side: TeamSide, worldX: number): FieldThird {
    const progress = this.relativeProgress(side, worldX);
    return progress >= this.fieldThirds.finalStart
      ? 'FINAL'
      : progress >= this.fieldThirds.defensiveEnd ? 'MIDDLE' : 'DEFENSIVE';
  }

  classifyLateralChannel(y: number): LateralChannel {
    if (y < this.lateralBands.leftWingEnd) return 'LEFT_WING';
    if (y < this.lateralBands.leftHalfSpaceEnd) return 'LEFT_HALF_SPACE';
    if (y <= this.lateralBands.centralEnd) return 'CENTRAL';
    if (y <= this.lateralBands.rightHalfSpaceEnd) return 'RIGHT_HALF_SPACE';
    return 'RIGHT_WING';
  }

  isInOwnHalf(side: TeamSide, position: Vector2): boolean {
    return this.relativeProgress(side, position.x) < this.functionalProgress.ownHalfEnd;
  }

  isInOffensiveZone(side: TeamSide, position: Vector2): boolean {
    return this.relativeProgress(side, position.x) >= this.functionalProgress.offensiveStart;
  }

  isInShootingZone(side: TeamSide, position: Vector2): boolean {
    return this.relativeProgress(side, position.x) >= this.functionalProgress.shootingStart
      && position.y >= this.penaltyArea.minY && position.y <= this.penaltyArea.maxY;
  }

  isInOwnPenaltyArea(side: TeamSide, position: Vector2): boolean {
    return this.relativeProgress(side, position.x) <= this.penaltyArea.depth
      && position.y >= this.penaltyArea.minY && position.y <= this.penaltyArea.maxY;
  }

  isInOpponentPenaltyArea(side: TeamSide, position: Vector2): boolean {
    return this.relativeProgress(side, position.x) >= this.length - this.penaltyArea.depth
      && position.y >= this.penaltyArea.minY && position.y <= this.penaltyArea.maxY;
  }

  isExtremeWide(position: Vector2): boolean {
    return position.y <= this.extremeWide.maximumLeftY || position.y >= this.extremeWide.minimumRightY;
  }

  isOutside(position: Vector2): boolean {
    const bounds = this.playableBounds;
    return position.x < bounds.minX || position.x > bounds.maxX
      || position.y < bounds.minY || position.y > bounds.maxY;
  }

  clampPlayable(position: Vector2): Vector2 {
    return this.clampTo(position, this.playableBounds);
  }

  clampRestart(position: Vector2): Vector2 {
    return this.clampTo(position, this.restartBounds);
  }

  clampPrediction(position: Vector2): Vector2 {
    return this.clampTo(position, this.predictionBounds);
  }

  clampGoalkeeperPhysicalPosition(side: TeamSide, position: Vector2): Vector2 {
    const relativeX = clamp(
      this.relativeProgress(side, position.x),
      this.goalkeeper.physicalMinProgress,
      this.goalkeeper.physicalMaxProgress,
    );
    return {
      x: this.worldX(side, relativeX),
      y: clamp(position.y, this.goalkeeper.minY, this.goalkeeper.maxY),
    };
  }

  private clampTo(position: Vector2, bounds: PitchRectangle): Vector2 {
    return {
      x: clamp(position.x, bounds.minX, bounds.maxX),
      y: clamp(position.y, bounds.minY, bounds.maxY),
    };
  }
}

export const PITCH_GEOMETRY = new PitchGeometry();
